import { useCallback, useEffect, useState } from 'react';
import { SearchableSelect } from '@tpct/admin-ui';
import type { PortalBootstrapData } from '../types/window';
import { __ } from '@/hub-shared/utils/i18n';
import { fetchWithNonce } from '../utils/fetchWithNonce';
import { parseApiError } from '../utils/parseApiError';

type EligibleMember = { id: number; display_name: string };

export type TransferOwnershipSectionProps = {
  bootstrap: PortalBootstrapData;
  onSessionExpired: () => void;
};

const TransferOwnershipSection = ({ bootstrap, onSessionExpired }: TransferOwnershipSectionProps) => {
  const [members, setMembers] = useState<EligibleMember[] | null>(null);
  const [newOwnerId, setNewOwnerId] = useState<string>('');
  const [previousRole, setPreviousRole] = useState<string>('expert_comptable');
  const [confirmed, setConfirmed] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [showTransferForm, setShowTransferForm] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [forbidden, setForbidden] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  useEffect(() => {
    const baseUrl = bootstrap.restUrls.accountingHub;
    fetchWithNonce(`${baseUrl}ownership-transfer/eligible-members`, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { 'X-WP-Nonce': bootstrap.nonce },
    })
      .then((r) => {
        if (r.status === 401) {
          onSessionExpired();
          return null;
        }
        if (r.status === 403) {
          setForbidden(true);
          return null;
        }
        if (!r.ok) return null;
        return r.json();
      })
      .then((data: EligibleMember[] | null) => {
        if (Array.isArray(data)) setMembers(data);
      })
      .catch(() => {});
  }, [bootstrap.nonce, bootstrap.restUrls.accountingHub, onSessionExpired]);

  const handleSubmit = useCallback(() => {
    if (!confirmed || !newOwnerId || submitting) return;
    setSubmitting(true);
    setError('');

    fetchWithNonce(`${bootstrap.restUrls.accountingHub}ownership-transfer/request`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-WP-Nonce': bootstrap.nonce,
      },
      body: JSON.stringify({
        new_owner_id: parseInt(newOwnerId, 10),
        previous_owner_role: previousRole,
      }),
    })
      .then(async (response) => {
        if (response.status === 401) {
          onSessionExpired();
          return;
        }

        if (response.ok) {
          setShowConfirmModal(false);
          setSuccessMessage(__('Invitation envoyée.'));
          setSubmitting(false);
          setConfirmed(false);
          setNewOwnerId('');
          return;
        }

        setError(await parseApiError(response));
        setSubmitting(false);
      })
      .catch(() => {
        setError(__('Le transfert a échoué.'));
        setSubmitting(false);
      });
  }, [bootstrap.nonce, bootstrap.restUrls.accountingHub, confirmed, newOwnerId, onSessionExpired, previousRole, submitting]);

  if (forbidden || members === null) return null;

  return (
    <div className="user-profile-transfer container-style" style={{ marginTop: '24px' }}>
      <h4>{__('Transférer la propriété du cabinet')}</h4>
      <button
        type="button"
        className="btn-secondary"
        onClick={() => setShowTransferForm((v) => !v)}
        aria-expanded={showTransferForm}
      >
        {showTransferForm ? __('Masquer') : __('Transférer la propriété')}
      </button>

      {showTransferForm && (
        <>
          {error ? <p className="accounting-hub-notice accounting-hub-notice-error">{error}</p> : null}
          {successMessage ? (
            <p className="accounting-hub-notice accounting-hub-notice-success" aria-live="polite">
              {successMessage}
            </p>
          ) : null}

          <div className="form-container user-profile-info">
            <div className="user-profile-info-row">
              <div className="form-field">
                <label htmlFor="transfer_new_owner">{__('Nouveau propriétaire')}</label>
                <SearchableSelect
                  id="transfer_new_owner"
                  options={members.map((m) => ({
                    value: String(m.id),
                    label: m.display_name || `#${m.id}`,
                  }))}
                  value={newOwnerId}
                  onChange={setNewOwnerId}
                  placeholder={`— ${__('Choisir')} —`}
                  searchPlaceholder={__('Rechercher...')}
                  noResultsText={__('Aucun membre trouvé')}
                />
              </div>
              <div className="form-field">
                <label htmlFor="transfer_previous_role">{__('Votre rôle après le transfert')}</label>
                <select
                  id="transfer_previous_role"
                  value={previousRole}
                  onChange={(e) => setPreviousRole(e.target.value)}
                >
                  <option value="expert_comptable">{__('Expert comptable')}</option>
                  <option value="collaborateur_comptable">{__('Collaborateur comptable')}</option>
                </select>
              </div>
            </div>
            <div className="checkbox-container">
              <input
                type="checkbox"
                id="transfer_confirm"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
              />
              <label htmlFor="transfer_confirm">
                {__(
                  "Je comprends que j'envoie une demande de transfert. Le transfert ne sera effectif qu'après acceptation par le destinataire, et je n'aurai plus accès à la facturation."
                )}
              </label>
            </div>
          </div>

          <button
            type="button"
            className="btn-primary"
            disabled={!confirmed || !newOwnerId || submitting}
            onClick={() => setShowConfirmModal(true)}
          >
            {submitting ? __('Envoi...') : __('Envoyer la demande')}
          </button>
        </>
      )}

      {showConfirmModal && (
        <div
          className="thn-modal-overlay"
          role="presentation"
          onClick={() => setShowConfirmModal(false)}
        >
          <div
            className="thn-modal thn-modal--small"
            role="dialog"
            aria-modal="true"
            aria-labelledby="transfer-confirm-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="thn-modal__close"
              onClick={() => setShowConfirmModal(false)}
              aria-label={__('Fermer')}
            >
              <span aria-hidden>×</span>
            </button>
            <div className="thn-modal__header">
              <h3 id="transfer-confirm-title">{__('Envoyer la demande de transfert')}</h3>
            </div>
            <div className="thn-modal__body">
              <p>
                {members
                  ? `${__('Une invitation sera envoyée à')} ${members.find((m) => String(m.id) === newOwnerId)?.display_name || `#${newOwnerId}`} ${__('pour accepter le transfert de propriété du cabinet.')}`
                  : __('Une invitation sera envoyée pour accepter le transfert de propriété du cabinet.')}
              </p>
            </div>
            <div className="thn-modal__footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowConfirmModal(false)}
              >
                {__('Annuler')}
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={submitting}
                onClick={() => {
                  setShowConfirmModal(false);
                  void handleSubmit();
                }}
              >
                {submitting ? __('Envoi...') : __('Envoyer la demande')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransferOwnershipSection;
